import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Grid,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { industryAndServiceKeys, CustomField } from '../../types/user';

interface Props {
  firstName: string;
  lastName: string;
  serviceType: CustomField;
  specialization: string;
  experience: string;
  servicesDescription: string;
  onInputChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (field: string) => (event: SelectChangeEvent, isCustomField?: boolean) => void;
}

const ContractorFields: React.FC<Props> = ({
  firstName,
  lastName,
  serviceType,
  specialization,
  experience,
  servicesDescription,
  onInputChange,
  onSelectChange,
}) => {
  const { t } = useTranslation();

  return (
    <Paper elevation={0} className="glass-effect" sx={{ p: 3, borderRadius: 2 }}>
      <Typography 
        variant="h6" 
        className="animated-gradient-text" 
        gutterBottom 
        sx={{ 
          mb: 4,
          fontFamily: 'Poppins, sans-serif'
        }}
      >
        {t('registration.generalInfo')}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('registration.lastName')}
            value={lastName}
            onChange={onInputChange('lastName')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('registration.firstName')}
            value={firstName}
            onChange={onInputChange('firstName')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>{t('registration.serviceType')}</InputLabel>
            <Select
              value={serviceType.value}
              onChange={(e) => onSelectChange('serviceType')(e, true)}
              label={t('registration.serviceType')}
            >
              {industryAndServiceKeys.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`registration.industries.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {serviceType.isOther && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('registration.industries.other_placeholder')}
              value={serviceType.customValue || ''}
              onChange={onInputChange('serviceType.customValue')}
              required
              variant="outlined"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.specialization')}
            value={specialization}
            onChange={onInputChange('specialization')}
            variant="outlined"
            inputProps={{ maxLength: 50 }}
            helperText={`${specialization.length}/50`}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.experience')}
            value={experience}
            onChange={onInputChange('experience')}
            required
            variant="outlined"
            type="number"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('registration.servicesDescription')}
            value={servicesDescription}
            onChange={onInputChange('servicesDescription')}
            required
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ContractorFields;
